import {
  consumeConcatenatedJsonObjects,
  extractConcatenatedJsonObjects,
  isRecord,
  parseSsePayload,
  resolveReplyEnvelope,
  type ChatBlock,
} from "./chatBlocks";
import { readSseEvents } from "./sse";

export type ChatError = {
  code: string;
  message: string;
  retryable: boolean;
};

export type ChatContext = {
  used: number;
  softLimit: number;
  hardLimit: number;
  percent: number;
  suggestCompact: boolean;
  limitReached: boolean;
};

export type StreamOutcome =
  | { status: "completed"; createdAt?: string }
  | { status: "stopped" }
  | { status: "failed"; error: ChatError; context?: ChatContext };

export type ChatHistoryEntry = {
  role: "user" | "assistant";
  blocks: ChatBlock[];
  createdAt?: string;
};

const ERRORS: Record<string, ChatError> = {
  network: {
    code: "network",
    message: "Couldn't reach Vecta. Check your connection and try again.",
    retryable: true,
  },
  session_expired: {
    code: "session_expired",
    message: "Your session has expired. Refresh the page to sign in again.",
    retryable: false,
  },
  forbidden: {
    code: "forbidden",
    message: "Ask Vecta isn't available on your account.",
    retryable: false,
  },
  nothing_to_regenerate: {
    code: "nothing_to_regenerate",
    message: "There's nothing to regenerate yet.",
    retryable: false,
  },
  chat_limit_reached: {
    code: "chat_limit_reached",
    message: "This chat has reached its length limit. Summarise it and continue in a new chat, or start a new chat.",
    retryable: false,
  },
  message_too_long: {
    code: "message_too_long",
    message: "That message is too long. Shorten it and try again.",
    retryable: false,
  },
  chat_busy: {
    code: "chat_busy",
    message: "Vecta is still working on your previous message. Try again in a moment.",
    retryable: true,
  },
  rate_limited: {
    code: "rate_limited",
    message: "Vecta is busy right now. Wait a moment and try again.",
    retryable: true,
  },
  timeout: {
    code: "timeout",
    message: "Vecta took too long to reply. Try again.",
    retryable: true,
  },
  interrupted: {
    code: "interrupted",
    message: "The reply was interrupted. Try again.",
    retryable: true,
  },
  failed: {
    code: "failed",
    message:
      "Sorry, I wasn't able to complete that just now. Please try again in a moment, or rephrase your question.",
    retryable: true,
  },
};

export const errorForStatus = (status: number): ChatError => {
  switch (status) {
    case 401:
      return ERRORS.session_expired;
    case 403:
      return ERRORS.forbidden;
    case 409:
      return ERRORS.nothing_to_regenerate;
    case 429:
      return ERRORS.rate_limited;
    case 408:
    case 504:
      return ERRORS.timeout;
    default:
      return ERRORS.failed;
  }
};

const isCount = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

export const parseChatContext = (value: unknown): ChatContext | undefined => {
  if (!isRecord(value)) return undefined;
  const { used, softLimit, hardLimit, percent, suggestCompact, limitReached } = value;
  if (!isCount(used) || !isCount(softLimit) || !isCount(hardLimit) || !isCount(percent)) return undefined;
  return {
    used,
    softLimit,
    hardLimit,
    percent: Math.min(100, Math.max(0, Math.round(percent))),
    suggestCompact: suggestCompact === true,
    limitReached: limitReached === true,
  };
};

const readUsageContext = (data: string): ChatContext | undefined => {
  try {
    const parsed: unknown = JSON.parse(data);
    return isRecord(parsed) ? parseChatContext(parsed.context) : undefined;
  } catch {
    return undefined;
  }
};

export type ConflictResult = { error: ChatError; context?: ChatContext };

export const readConflict = async (response: Response): Promise<ConflictResult> => {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { error: ERRORS.nothing_to_regenerate };
  }
  if (!isRecord(body)) return { error: ERRORS.nothing_to_regenerate };

  const message = typeof body.message === "string" && body.message ? body.message : undefined;
  switch (body.code) {
    case "chat_limit_reached":
      return {
        error: message ? { ...ERRORS.chat_limit_reached, message } : ERRORS.chat_limit_reached,
        context: parseChatContext(body.context),
      };
    case "chat_busy":
      return { error: ERRORS.chat_busy };
    case "message_too_long":
      return { error: message ? { ...ERRORS.message_too_long, message } : ERRORS.message_too_long };
          default:
            return { error: ERRORS.nothing_to_regenerate };
  }
};

const errorForCode = (code: string | undefined): ChatError =>
  code === "timeout" ? ERRORS.timeout : ERRORS.failed;

const parseErrorEvent = (data: string): ChatError => {
  try {
    const parsed: unknown = JSON.parse(data);
    if (isRecord(parsed) && typeof parsed.code === "string") {
      const error = errorForCode(parsed.code);
      return parsed.retryable === false ? { ...error, retryable: false } : error;
    }
  } catch {
    // fall through to the generic error
  }
  return ERRORS.failed;
};

export const buildChatHeaders = (): Record<string, string> => {
  const token = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value;
  return {
    "Content-Type": "application/json",
    ...(token ? { RequestVerificationToken: token } : {}),
  };
};

const isEventStream = (response: Response): boolean =>
  response.headers.get("content-type")?.toLowerCase().includes("text/event-stream") ?? false;

const readCreatedAt = (data: string): string | undefined => {
  try {
    const parsed: unknown = JSON.parse(data);
    return isRecord(parsed) && typeof parsed.createdAt === "string" ? parsed.createdAt : undefined;
  } catch {
    return undefined;
  }
};

export type PendingKind = "table" | "chart" | "list";

const PENDING_KINDS: readonly string[] = ["table", "chart", "list"];
const TYPE_PATTERN = /"type"\s*:\s*"([a-z_]+)"/;

export const detectPendingKind = (pending: string): PendingKind | null => {
  const match = TYPE_PATTERN.exec(pending);
  return match && PENDING_KINDS.includes(match[1]) ? (match[1] as PendingKind) : null;
};

const readStatusLabel = (data: string): string | null => {
  try {
    const parsed: unknown = JSON.parse(data);
    return isRecord(parsed) && typeof parsed.label === "string" && parsed.label ? parsed.label : null;
  } catch {
    return null;
  }
};

export type StreamChatReplyOptions = {
  url: string;
  body: Record<string, unknown>;
  signal: AbortSignal;
  onBlocks: (blocks: ChatBlock[]) => void;
  onPending?: (kind: PendingKind | null) => void;
  onStatus?: (label: string | null) => void;
  onContext?: (context: ChatContext) => void;
};

export const streamChatReply = async ({
  url,
  body,
  signal,
  onBlocks,
  onPending,
  onStatus,
  onContext,
}: StreamChatReplyOptions): Promise<StreamOutcome> => {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: buildChatHeaders(),
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    return signal.aborted ? { status: "stopped" } : { status: "failed", error: ERRORS.network };
  }

  if (response.status === 409 || response.status === 400) {
    const { error, context } = await readConflict(response);
    return { status: "failed", error: response.status === 400 && error.code === "nothing_to_regenerate" ? ERRORS.failed : error, context };
  }

  if (!response.ok) {
    return { status: "failed", error: errorForStatus(response.status) };
  }

  if (!response.body || !isEventStream(response)) {
    return { status: "failed", error: ERRORS.session_expired };
  }

  const emit = (blocks: ChatBlock[]) => {
    if (blocks.length > 0) onBlocks(blocks);
  };

  let pending = "";
  let envelopeResolved = false;
  let completed = false;
  let createdAt: string | undefined;
  let reportedKind: PendingKind | null = null;

  const reportPending = () => {
    const kind = detectPendingKind(pending);
    if (kind === reportedKind) return;
    reportedKind = kind;
    onPending?.(kind);
  };

  const flushPending = () => {
    const { objects, remaining } = consumeConcatenatedJsonObjects(pending);
    const blocks = objects.flatMap(parseSsePayload);
    if (remaining.trim().length > 0) blocks.push(...parseSsePayload(remaining));
    pending = "";
    emit(blocks);
  };

  try {
    for await (const event of readSseEvents(response.body, signal)) {
      if (event.event === "usage") {
        const context = readUsageContext(event.data);
        if (context) onContext?.(context);
        continue;
      }

      if (event.event === "status") {
        onStatus?.(readStatusLabel(event.data));
        continue;
      }

      if (event.event === "error") {
        return { status: "failed", error: parseErrorEvent(event.data) };
      }

      if (event.event === "done") {
        completed = true;
        createdAt = readCreatedAt(event.data);
        break;
      }

      if (!event.data || event.data.trim() === "done") continue;

      onStatus?.(null);
      pending += event.data;
      if (!envelopeResolved) {
        const envelope = resolveReplyEnvelope(pending);
        envelopeResolved = envelope.resolved;
        pending = envelope.text;
      }
      const { objects, remaining } = consumeConcatenatedJsonObjects(pending);
      pending = remaining;
      emit(objects.flatMap(parseSsePayload));
      reportPending();
    }
  } catch {
    return signal.aborted ? { status: "stopped" } : { status: "failed", error: ERRORS.interrupted };
  }

  if (signal.aborted) return { status: "stopped" };

  flushPending();
  reportPending();

  return completed
    ? { status: "completed", createdAt }
    : { status: "failed", error: ERRORS.interrupted };
};

const toHistoryEntry = (data: string): ChatHistoryEntry | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }

  if (!isRecord(parsed) || typeof parsed.text !== "string") return null;
  if (parsed.role !== "user" && parsed.role !== "assistant") return null;

  const blocks: ChatBlock[] =
    parsed.role === "assistant"
      ? extractConcatenatedJsonObjects(parsed.text)
          .flatMap((object) => {
            try {
              return parseSsePayload(object);
            } catch {
              return [];
            }
          })
          .filter((block) => block.type !== "done")
      : [{ type: "text", content: parsed.text }];

  return {
    role: parsed.role,
    blocks,
    createdAt: typeof parsed.createdAt === "string" ? parsed.createdAt : undefined,
  };
};

export const loadChatHistory = async (
  signal: AbortSignal,
  onContext?: (context: ChatContext) => void
): Promise<ChatHistoryEntry[]> => {
  const response = await fetch("/api/ai/chatsession", { headers: buildChatHeaders(), signal });
  if (!response.ok || !response.body || !isEventStream(response)) return [];

  const history: ChatHistoryEntry[] = [];
  for await (const event of readSseEvents(response.body, signal)) {
    if (event.event === "usage") {
      const context = readUsageContext(event.data);
      if (context) onContext?.(context);
      continue;
    }
    if (event.event !== "message") continue;
    const entry = toHistoryEntry(event.data);
    if (entry) history.push(entry);
  }
  return history;
};

export type CompactOutcome =
  | { status: "compacted"; context?: ChatContext }
  | { status: "failed"; error: ChatError };

const COMPACT_ERRORS: Record<string, ChatError> = {
  nothing_to_compact: {
    code: "nothing_to_compact",
    message: "There's nothing to summarise yet.",
    retryable: false,
  },
  compact_failed: {
    code: "compact_failed",
    message: "Couldn't summarise this chat. Your chat is unchanged - try again.",
    retryable: true,
  },
};

export const compactChat = async (url: string): Promise<CompactOutcome> => {
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", headers: buildChatHeaders() });
  } catch {
    return { status: "failed", error: ERRORS.network };
  }

  if (response.status === 409) {
    const body: unknown = await response.json().catch(() => null);
    const code = isRecord(body) ? body.code : undefined;
    if (code === "nothing_to_compact") return { status: "failed", error: COMPACT_ERRORS.nothing_to_compact };
    if (code === "chat_busy") return { status: "failed", error: ERRORS.chat_busy };
    return { status: "failed", error: COMPACT_ERRORS.compact_failed };
  }

  if (!response.ok) {
    const known = [401, 403].includes(response.status) ? errorForStatus(response.status) : null;
    return { status: "failed", error: known ?? COMPACT_ERRORS.compact_failed };
  }

  const body: unknown = await response.json().catch(() => null);
  return { status: "compacted", context: isRecord(body) ? parseChatContext(body.context) : undefined };
};
